import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import { Mission } from '../api/missions'
import {
  abandon,
  addEventsToWorkPeriods,
  addTagsToWorkPeriods,
  cancel,
  create,
  fetch,
  get,
  materializeSingleDay,
  MaterializeSingleDayPayload,
  remove,
  update,
  updateTags,
  validate,
  validateMany,
  WorkPeriodAddEventsToWorkPeriodsPayload,
  WorkPeriodAddTagsToWorkPeriodsPayload,
  WorkPeriodCreationPayload,
  WorkPeriodCreationResponse,
  WorkPeriodGetParams,
  WorkPeriodRemovePayload,
  WorkPeriodUpdatePayload,
  WorkPeriodUpdateTagsPayload,
  WorkPeriodValidateManyPayload,
  WorkPeriodValidatePayload,
  WorkPeriodWithMission,
  WorkPeriodWithMissionAndSummary,
} from '../api/work-periods'
import { invalidateMissionsQueries } from './missions'
import { useNormalizeEventsFilters } from '../hooks/use-normalize-events-filters'

export const workPeriodKeys = {
  all: ['work-periods'] as const,
  detail: (id: string) => ['work-periods', id] as const,
  mutations: {
    update: ['missions', 'work-periods', 'update'] as const,
    create: ['missions', 'work-periods', 'create'] as const,
    validate: ['missions', 'work-periods', 'validate'] as const,
    cancel: ['missions', 'work-periods', 'cancel'] as const,
    abandon: ['missions', 'work-periods', 'abandon'] as const,
    remove: ['missions', 'work-periods', 'remove'] as const,
    addEvents: ['missions', 'work-periods', 'add-events'] as const,
    validateMany: ['missions', 'work-periods', 'validate-many'] as const,
    updateTags: ['work-periods', 'update-tags'] as const,
    addTags: ['missions', 'work-periods', 'add-tags'] as const,
    materializeDay: ['missions', 'work-periods', 'materialize-day'] as const,
  },
} as const

//Queries

/**
 * Fetch a single work period by ID.
 * Uses GET /work-periods/:id with optional ?summary=true.
 *
 * @param id - The work period ID. Query is disabled when undefined.
 * @param params - Optional query params (e.g. { summary: true }).
 * @param options - React Query options override.
 * @returns The work period with its populated mission (and summary when requested).
 */
export const useWorkPeriodQuery = (
  id?: string,
  params?: WorkPeriodGetParams,
  options?: QueryOptions<WorkPeriodWithMission>,
): UseQueryResult<WorkPeriodWithMission, any> =>
  useResource<WorkPeriodWithMission, WorkPeriodGetParams>('work-periods', get, id, params, options)

/**
 * Same as useWorkPeriodQuery but typed with summary included.
 * Automatically passes summary=true.
 *
 * @param id - The work period ID. Query is disabled when undefined.
 * @param options - React Query options override.
 */
export const useWorkPeriodWithSummaryQuery = (
  id?: string,
  options?: QueryOptions<WorkPeriodWithMissionAndSummary>,
): UseQueryResult<WorkPeriodWithMissionAndSummary, any> =>
  useResource<WorkPeriodWithMissionAndSummary, WorkPeriodGetParams>(
    'work-periods',
    get as (params: { id: string } & WorkPeriodGetParams) => Promise<WorkPeriodWithMissionAndSummary>,
    id,
    { summary: true },
    options,
  )

export const useWorkPeriodsQuery = (
  filters?: any, // TODO: type !!!
  options?: QueryOptions<WorkPeriodWithMission[]>,
): UseQueryResult<WorkPeriodWithMission[], any> => {
  // Normalize events filters (transform sentinels to actual event IDs)
  const normalizedEvents = useNormalizeEventsFilters(filters?.events)

  // Merge normalized events into filters
  const normalizedFilters = {
    ...filters,
    ...normalizedEvents,
  }

  return useResources<WorkPeriodWithMission[], any>(
    'work-periods',
    fetch as unknown as (params: any) => Promise<WorkPeriodWithMission[]>,
    normalizedFilters,
    options,
  )
}

//Mutations
export const useUpdateMutation = (): UseMutationResult<Mission, WorkPeriodUpdatePayload> =>
  useMutation(update, {
    mutationKey: workPeriodKeys.mutations.update,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useCreateWorkPeriodMutation = (): UseMutationResult<
  WorkPeriodCreationResponse,
  WorkPeriodCreationPayload
> =>
  useMutation(create, {
    mutationKey: workPeriodKeys.mutations.create,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useValidateMutation = (): UseMutationResult<Mission, WorkPeriodValidatePayload> =>
  useMutation(validate, {
    mutationKey: workPeriodKeys.mutations.validate,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useCancelMutation = () =>
  useMutation(cancel, {
    mutationKey: workPeriodKeys.mutations.cancel,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useAbandonMutation = () =>
  useMutation(abandon, {
    mutationKey: workPeriodKeys.mutations.abandon,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useRemoveWorkPeriodMutation = (): UseMutationResult<
  Mission,
  WorkPeriodRemovePayload
> =>
  useMutation(remove, {
    mutationKey: workPeriodKeys.mutations.remove,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useAddEventsToWorkPeriodsMutation = (): UseMutationResult<
  Mission,
  WorkPeriodAddEventsToWorkPeriodsPayload
> =>
  useMutation(addEventsToWorkPeriods, {
    mutationKey: workPeriodKeys.mutations.addEvents,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useValidateManyMutation = (): UseMutationResult<
  Mission,
  WorkPeriodValidateManyPayload
> =>
  useMutation(validateMany, {
    mutationKey: workPeriodKeys.mutations.validateMany,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useUpdateTagsMutation = (): UseMutationResult<
  WorkPeriodWithMission,
  WorkPeriodUpdateTagsPayload
> =>
  useMutation(updateTags, {
    mutationKey: workPeriodKeys.mutations.updateTags,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const useAddTagsToWorkPeriodsMutation = (): UseMutationResult<
  Mission,
  WorkPeriodAddTagsToWorkPeriodsPayload
> =>
  useMutation(addTagsToWorkPeriods, {
    mutationKey: workPeriodKeys.mutations.addTags,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

/**
 * Mutation hook to materialize a single day's work period for a template-based mission.
 *
 * POST /api/missions/:missionId/materialize-day
 *
 * On success, invalidates both work-periods and missions queries so lists stay fresh.
 */
export const useMaterializeSingleDayMutation = (): UseMutationResult<
  WorkPeriodWithMission | null,
  MaterializeSingleDayPayload
> =>
  useMutation(materializeSingleDay, {
    mutationKey: workPeriodKeys.mutations.materializeDay,
    onSuccess: () => {
      invalidateWorkPeriodsQueries()
      invalidateMissionsQueries()
    },
  })

export const invalidateWorkPeriodsQueries = (): void => {
  queryClient.invalidateQueries(workPeriodKeys.all)
}
